'use client';

import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@lulalife/design-system';
import { useEffect, useState } from 'react';

type SettingsDeleteRoleModalProps = {
  open: boolean;
  roleName: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
  'data-testid'?: string;
};

/**
 * The type-to-confirm delete modal Figma specs for a role with no users on
 * it — PEG-861/862. Built on the design system's own Modal, not the app-wide
 * reactstrap `confirmationModal` this replaces: the Role pages are already a
 * DS/Tailwind surface, so their one destructive action gets the DS's own
 * primitives instead of borrowing a legacy global.
 *
 * "Delete" stays disabled until the typed text matches the role name exactly
 * — the same type-to-confirm safety the legacy modal enforced, reimplemented
 * here rather than carried over with it.
 */
export const SettingsDeleteRoleModal = ({
  open,
  roleName,
  isDeleting,
  onConfirm,
  onClose,
  'data-testid': dataTestId,
}: SettingsDeleteRoleModalProps) => {
  const [confirmText, setConfirmText] = useState('');

  // Re-armed on every open, not just every roleName — reopening on the same
  // role after a Close must not leave a stale match sitting in the field.
  useEffect(() => {
    if (!open) setConfirmText('');
  }, [open]);

  const canConfirm = confirmText === roleName;

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      size="small"
      data-lula-ds=""
      data-testid={dataTestId}
    >
      <ModalHeader>
        <ModalTitle>Delete role</ModalTitle>
        <ModalDescription>
          Do you want to delete role {roleName}? Type the role name to confirm.
        </ModalDescription>
      </ModalHeader>
      <ModalBody>
        <Input
          label="Role name"
          required
          value={confirmText}
          placeholder={`Type ${roleName} here to delete this role`}
          disabled={isDeleting}
          data-testid={dataTestId ? `${dataTestId}.confirm-input` : undefined}
          onChange={(event) => setConfirmText(event.target.value)}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" type="button" disabled={isDeleting} onClick={onClose}>
          Close
        </Button>
        <Button
          variant="destructive"
          type="button"
          disabled={!canConfirm}
          isLoading={isDeleting}
          onClick={onConfirm}
        >
          Delete
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SettingsDeleteRoleModal;
